<template>
  <AppChat />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { useInitialCookies } from './composables/general/useInitialCookies';

const { $api } = useNuxtApp();
const { marketingConsented } = useCookieConsent();
const scrollLockStore = useMyScrollLockStore();
const { pageShouldBeScrollLocked } = storeToRefs(scrollLockStore);
useScrollLockForCookieConsent();
const config = useRuntimeConfig();
const planStore = usePlanStore();
const analyticsEventsStore = useAnalyticsEventsStore();
useInitialCookies();

const { eventsToPushToDatalayer } = storeToRefs(analyticsEventsStore);

if (config.public.onetrustCookieConsentDomainScript !== 'skipOneTrustCookie') {
  useScript({
    src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
    type: 'text/javascript',
    async: true,
    defer: true,
    'data-language': 'sv',
    'data-domain-script': config.public.onetrustCookieConsentDomainScript || '',
  });
}

useScript(
  {
    src: '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
    type: 'text/javascript',
    tagPosition: 'bodyClose',
    async: true,
    referrerpolicy: false,
    crossorigin: false,
  },
  {
    trigger: marketingConsented,
  },
);

const load = useScript(
  {
    src: `https://collect.halebop.se/gtm.js?id=GTM-PTXPXGR&l=dataLayer`,
    type: 'text/javascript',
    async: true,
    tagPosition: 'bodyClose',
    crossorigin: false,
  },
  {
    trigger: 'manual',
  },
);

onMounted(() => {
  if (import.meta.client) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    load.load();
  }
});

await useAsyncData('plans', async () => {
  const plansResponse = await $api.planModule.getAllPlans();
  planStore.processAndSetPlans(plansResponse);
  return plansResponse;
});

watch(pageShouldBeScrollLocked, (value: boolean) => {
  document.body.style.overflow = value ? 'hidden' : '';
});

watch(
  eventsToPushToDatalayer,
  (newEvents, previousEvents) => {
    if (!import.meta.client) return;
    if (newEvents.length === previousEvents?.length) {
      return;
    }

    const events = newEvents.slice();
    let hasChangedState = false;

    for (const event of events) {
      if (event.isPushedToDatalayer === false) {
        hasChangedState = true;
        const { event: eventName, ...eventObjectData } = toRaw(
          event.eventObject,
        );
        const data = {
          event: eventName,
          ...eventObjectData,
        };

        window.dataLayer = window.dataLayer || [];
        if (Object.prototype.hasOwnProperty.call(data, 'ecommerce')) {
          window.dataLayer.push({
            ecommerce: null,
          });
        }

        window.dataLayer.push(data);
        event.isPushedToDatalayer = true;
      }
    }

    if (!hasChangedState) {
      return;
    }

    analyticsEventsStore.eventsToPushToDatalayer = events;
  },
  { immediate: true },
);
</script>
